import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatMarkdownLink from './ChatMarkdownLink';
import ActionConfirmCard from './ActionConfirmCard';
import { visibleChatText } from 'utils/chatDisplay';

export default function ChatMessage({ message, conversationId, onActionResolved }) {
  const { t } = useTranslation();
  const { role, content, reasoning, toolName, toolOutput } = message;
  const [reasoningOpen, setReasoningOpen] = useState(false);
  const { content: visibleContent, reasoning: visibleReasoning } = visibleChatText(content, reasoning);

  if (role === 'action_proposal') {
    return <ActionConfirmCard message={message} conversationId={conversationId} onResolved={onActionResolved} />;
  }

  if (role === 'tool') {
    let parsed = null;
    try {
      parsed = JSON.parse(toolOutput ?? '{}');
    } catch {
      /* raw */
    }
    return (
      <Box sx={{ mb: 1, px: 1 }}>
        <Chip size="small" label={toolName} sx={{ fontSize: 11, mb: 0.5 }} variant="outlined" color="default" />
        {parsed?.error ? (
          <Typography variant="caption" color="error" display="block">
            {parsed.error}
          </Typography>
        ) : (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', fontSize: 10 }}>
            {parsed?.rowCount !== undefined ? t('chat.rowsCount', { count: parsed.rowCount }) : t('chat.done')}
          </Typography>
        )}
      </Box>
    );
  }

  const isUser = role === 'user';

  if (!isUser && !visibleContent && !visibleReasoning) {
    return null;
  }
  const isStreaming = !visibleContent && !!visibleReasoning;
  const hasReasoning = !!visibleReasoning;

  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5, px: 1 }}>
      <Box
        sx={{
          maxWidth: '85%',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: isUser ? 'primary.main' : 'background.paper',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          border: isUser ? 'none' : '1px solid',
          borderColor: 'divider',
          '& p': { m: 0, mb: 0.5 },
          '& p:last-child': { mb: 0 },
          '& pre': { overflowX: 'auto', fontSize: 11, p: 1, borderRadius: 1, bgcolor: 'action.hover' },
          '& code': { fontSize: 11 },
          '& table': { borderCollapse: 'collapse', fontSize: 12, width: '100%' },
          '& th, & td': { border: '1px solid', borderColor: 'divider', px: 0.5, py: 0.25 },
          '& a': { color: 'primary.main' }
        }}
      >
        {!isUser && hasReasoning && (
          <Box sx={{ mb: visibleContent ? 1 : 0 }}>
            <Typography
              variant="caption"
              onClick={() => !isStreaming && setReasoningOpen((o) => !o)}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.disabled',
                fontSize: 10,
                fontStyle: 'italic',
                cursor: isStreaming ? 'default' : 'pointer',
                userSelect: 'none'
              }}
            >
              {isStreaming ? t('chat.thinking', 'Pensando…') : t('chat.showReasoning', 'Razonamiento')}
              {!isStreaming && <span style={{ fontSize: 8 }}>{reasoningOpen ? '▲' : '▼'}</span>}
            </Typography>
            <Collapse in={isStreaming || reasoningOpen}>
              <Typography
                variant="caption"
                component="div"
                sx={{
                  mt: 0.5,
                  color: 'text.disabled',
                  fontSize: 10,
                  fontStyle: 'italic',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  borderLeft: '2px solid',
                  borderColor: 'divider',
                  pl: 1
                }}
              >
                {visibleReasoning}
              </Typography>
            </Collapse>
          </Box>
        )}

        {isUser ? (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {visibleContent}
          </Typography>
        ) : visibleContent ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ a: ChatMarkdownLink }}>
            {visibleContent}
          </ReactMarkdown>
        ) : isStreaming ? null : (
          <Typography variant="body2" color="text.secondary">
            …
          </Typography>
        )}
      </Box>
    </Box>
  );
}
